import { BN_POSTS } from "@/content/bn/posts";
import { isPairedCategory, isPairedCity } from "@/content/language-pairs";

/**
 * Maps a pathname to its counterpart in the other language.
 *
 * Only pairs that actually resolve are returned; anything unpaired falls back
 * to the other language's home ("/bn" or "/"). The pairing sources here are the
 * same ones that drive hreflang (`src/content/language-pairs.ts` and the
 * `enPath` field on Bangla posts), so the switcher and the alternate tags can't
 * disagree.
 */

/** Sections that exist verbatim in both languages: `/x` ↔ `/bn/x`. */
const MIRRORED_SECTIONS = [
  "about",
  "blog",
  "book",
  "case-studies",
  "contact",
  "pricing",
  "seo-services",
  "services",
  "free-seo-audit",
  "ai-action-plan",
  "online-marketing-small-businesses",
] as const;

/**
 * Every English top-level section reachable from the header nav, mapped to the
 * closest Bangla page that actually exists. Bangla has fewer pages than
 * English, so unpaired sections resolve to their nearest Bangla sibling (the
 * services hub, the case-study index, etc.) instead of dumping the visitor on
 * the Bangla home. Keyed by first path segment.
 */
const SECTION_TO_BN: Record<string, string> = {
  // Services cluster
  services: "/bn/services",
  pricing: "/bn/pricing",
  "seo-for": "/bn/services",
  "seo-training": "/bn/services",
  "free-seo-audit": "/bn/services",
  aeo: "/bn/services",
  // Locations cluster
  "seo-locations": "/bn/seo-services",
  "seo-services": "/bn/seo-services",
  // Work cluster
  "case-studies": "/bn/case-studies",
  testimonials: "/bn/case-studies",
  process: "/bn/case-studies",
  proof: "/bn/case-studies",
  credentials: "/bn/about",
  // Resources / tools cluster
  blog: "/bn/blog",
  resources: "/bn/blog",
  "seo-tools": "/bn/blog",
  tools: "/bn/blog",
  glossary: "/bn/blog",
  compare: "/bn/blog",
  "ai-answers": "/bn/blog",
  "bangladesh-seo": "/bn/blog",
  "seo-expert-bangladesh": "/bn/blog",
  "best-digital-marketing-expert-bangladesh": "/bn/blog",
  "what-is-digital-marketing": "/bn/blog",
  "how-to-improve-domain-authority": "/bn/blog",
  "how-to-check-domain-authority": "/bn/blog",
  "moz-da-vs-ahrefs-dr": "/bn/blog",
  // Company
  about: "/bn/about",
  book: "/bn/book",
  contact: "/bn/contact",
  sitemap: "/bn",
  privacy: "/bn",
  trust: "/bn",
};


function normalize(pathname: string): string {
  const p = pathname.split("?")[0]!.split("#")[0]!;
  return p.length > 1 ? p.replace(/\/+$/, "") : p;
}

export function isBanglaPath(pathname: string): boolean {
  const p = normalize(pathname);
  return p === "/bn" || p.startsWith("/bn/");
}

/** English path -> Bangla blog post path, derived from each BN post's `enPath`. */
function bnPostForEnPath(enPath: string): string | undefined {
  const match = BN_POSTS.find((p) => p.enPath === enPath);
  return match ? `/bn/blog/${match.slug}` : undefined;
}

function enToBn(pathname: string): string {
  const p = normalize(pathname);
  if (p === "/") return "/bn";

  const seg = p.split("/").filter(Boolean);

  // Blog: category / post
  if (seg[0] === "blog") {
    if (seg[1] === "category" && seg[2]) {
      return isPairedCategory(seg[2]) ? `/bn/blog/category/${seg[2]}` : "/bn/blog";
    }
    if (seg[1]) return bnPostForEnPath(p) ?? "/bn/blog";
    return "/bn/blog";
  }

  // Paired service detail pages
  if (seg[0] === "services" && (seg[1] === "aeo" || seg[1] === "geo")) return `/bn/services/${seg[1]}`;

  // City hubs and city x service
  if (seg[0] === "seo-services" && seg[1]) {
    if (!isPairedCity(seg[1])) return "/bn/seo-services";
    return seg[2] ? `/bn/seo-services/${seg[1]}/${seg[2]}` : `/bn/seo-services/${seg[1]}`;
  }

  // Standalone English pages that have a full Bangla translation as a post
  const postPair = bnPostForEnPath(p);
  if (postPair) return postPair;

  if (seg.length === 1 && (MIRRORED_SECTIONS as readonly string[]).includes(seg[0]!)) {
    return `/bn/${seg[0]}`;
  }

  // Nearest Bangla sibling for everything else in the nav
  return SECTION_TO_BN[seg[0]!] ?? "/bn";
}


function bnToEn(pathname: string): string {
  const p = normalize(pathname);
  if (p === "/bn") return "/";

  const seg = p.split("/").filter(Boolean).slice(1); // drop "bn"

  if (seg[0] === "blog") {
    if (seg[1] === "category" && seg[2]) {
      return isPairedCategory(seg[2]) ? `/blog/category/${seg[2]}` : "/blog";
    }
    if (seg[1]) {
      const post = BN_POSTS.find((x) => x.slug === seg[1]);
      return post?.enPath ?? "/blog";
    }
    return "/blog";
  }

  if (seg[0] === "services" && seg[1]) {
    return seg[1] === "aeo" || seg[1] === "geo" ? `/services/${seg[1]}` : "/services";
  }

  if (seg[0] === "seo-services" && seg[1]) {
    if (!isPairedCity(seg[1])) return "/seo-services";
    return seg[2] ? `/seo-services/${seg[1]}/${seg[2]}` : `/seo-services/${seg[1]}`;
  }

  if (seg.length === 1 && (MIRRORED_SECTIONS as readonly string[]).includes(seg[0]!)) {
    return `/${seg[0]}`;
  }

  return "/";
}

/** Counterpart URL for the current path in the other language. */
export function counterpartPath(pathname: string): string {
  return isBanglaPath(pathname) ? bnToEn(pathname) : enToBn(pathname);
}
